// Pure geometry bridging the base's cell/edge model (base-walls.mjs, keyed in
// lattice row/col) to lon/lat on the live map. Extracted so the rotation +
// projection math is unit-testable in node and the map view (base-defense-
// view.mjs) stays thin. Every world conversion goes through makeToWorld,
// which is a verbatim port of building-footprint.mjs's baseShapeCellsToGeoJSON
// so walls/doors/defenders land exactly on the existing base-grid layer.

import { createLocalProjection, rotatePoint } from "./building-footprint.mjs";
import { cellKey, edgeCells, isExteriorEdge, parseCellKey } from "./base-walls.mjs";

// The two lattice endpoints (in [col, row]) of an edge. A vertical edge
// "v:r:c" separates (r,c) and (r,c+1), so it runs down the shared grid line
// at column c+1 from row r to row r+1; a horizontal edge "h:r:c" runs along
// the line at row r+1 from column c to column c+1.
export function latticeEndpoints(edgeKey) {
  const [kind, rowText, colText] = edgeKey.split(":");
  const row = Number(rowText);
  const col = Number(colText);
  if (kind === "v") return { a: [col + 1, row], b: [col + 1, row + 1] };
  return { a: [col, row + 1], b: [col + 1, row + 1] };
}

// (col, row) lattice point -> [lon, lat]. Fractional col/row are fine
// (cell centers, edge midpoints). Verbatim formula from
// baseShapeCellsToGeoJSON (building-footprint.mjs).
export function makeToWorld(shape) {
  const projection = createLocalProjection(shape.origin);
  return (col, row) => projection.toCoordinate(rotatePoint(
    [shape.minX + col * shape.cellSizeMeters, shape.minY + row * shape.cellSizeMeters],
    shape.angleDegrees,
  ));
}

// The exact inverse of makeToWorld: [lon, lat] -> fractional [col, row] in the
// base's cell lattice. Undo the projection to local metres, unrotate, then
// strip the origin offset and cell size. Flooring the result gives the integer
// (col, row) whose "row:col" key can be tested against shape.cells — an O(1)
// "is this world point inside the base interior?" check for the nav grid.
export function makeFromWorld(shape) {
  const projection = createLocalProjection(shape.origin);
  return (coordinate) => {
    const [x, y] = rotatePoint(projection.toMeters(coordinate), -shape.angleDegrees);
    return [(x - shape.minX) / shape.cellSizeMeters, (y - shape.minY) / shape.cellSizeMeters];
  };
}

// True when a world coordinate falls on an interior cell of the base shape.
export function baseInteriorTest(shape) {
  const fromWorld = makeFromWorld(shape);
  const cells = shape.cells instanceof Set ? shape.cells : new Set(shape.cells || []);
  return (coordinate) => {
    const [col, row] = fromWorld(coordinate);
    return cells.has(cellKey(Math.floor(row), Math.floor(col)));
  };
}

// LineString features for every wall and door, plus any breached doors drawn
// distinctly. properties.kind is "wall" | "door" | "breached".
export function wallStateToGeoJSON(shape, wallState, breachedEdgeKeys = []) {
  const toWorld = makeToWorld(shape);
  const breached = breachedEdgeKeys instanceof Set ? breachedEdgeKeys : new Set(breachedEdgeKeys);
  const doorSet = new Set(wallState?.doorEdgeKeys || []);
  const features = [];
  const lineFeature = (edgeKey, kind) => {
    const { a, b } = latticeEndpoints(edgeKey);
    return {
      type: "Feature",
      properties: { kind, edgeKey },
      geometry: { type: "LineString", coordinates: [toWorld(a[0], a[1]), toWorld(b[0], b[1])] },
    };
  };
  for (const edgeKey of wallState?.wallEdgeKeys || []) {
    if (doorSet.has(edgeKey)) continue; // a wall that's been cut into a door draws as a door
    features.push(lineFeature(edgeKey, "wall"));
  }
  for (const edgeKey of doorSet) {
    features.push(lineFeature(edgeKey, breached.has(edgeKey) ? "breached" : "door"));
  }
  return { type: "FeatureCollection", features };
}

// The door/window symbol for each intact opening. INTERIOR doors draw as the
// architectural swing: the door leaf (a line from the hinge, swung 90° open
// into a room) plus the quarter-circle swing arc. EXTERIOR entrances mostly
// draw as the classic window symbol instead -- three short parallel lines
// across the opening (properties.part === "window") -- so a base with several
// entrances doesn't read as a house full of doors; but 1 in every 5 exterior
// entrances stays a real door (a swing), door-first (entrance index 0, 5, 10,
// ... in generation order), so every base still has at least one actual door.
// Breached openings are skipped (drawn as a broken opening elsewhere).
export function doorSwingsToGeoJSON(shape, cells, wallState, breachedEdgeKeys = []) {
  const toWorld = makeToWorld(shape);
  const breached = breachedEdgeKeys instanceof Set ? breachedEdgeKeys : new Set(breachedEdgeKeys);
  const doorEdges = wallState?.doorEdgeKeys || [];
  const isExterior = (edgeKey) => isExteriorEdge(cells, edgeKey);
  // Every 5th exterior entrance (0, 5, 10, ...) stays a real door; the rest
  // are windows. Deterministic from generation order, so it's stable.
  const exteriorEntrances = doorEdges.filter(isExterior);
  const doorEntrances = new Set(exteriorEntrances.filter((_, index) => index % 5 === 0));

  const features = [];
  const ARC_STEPS = 10;
  for (const edgeKey of doorEdges) {
    if (breached.has(edgeKey)) continue;
    const { a, b } = latticeEndpoints(edgeKey); // jambs (1 cell apart)
    const [kind] = edgeKey.split(":");
    const [aCell, bCell] = edgeCells(edgeKey);
    const towardA = kind === "v" ? [-1, 0] : [0, -1];
    const towardB = kind === "v" ? [1, 0] : [0, 1];
    // Prefer swinging toward an inside cell; for an exterior door that forces
    // it inward. Default to the "b" side when both are inside.
    const swing = cells.has(cellKey(...bCell)) ? towardB : (cells.has(cellKey(...aCell)) ? towardA : towardB);

    // A window is any exterior entrance that isn't the 1-in-5 kept as a door:
    // three parallel lines straddling the wall line across the opening.
    if (isExterior(edgeKey) && !doorEntrances.has(edgeKey)) {
      for (const off of [-0.1, 0, 0.1]) {
        const p0 = [a[0] + swing[0] * off, a[1] + swing[1] * off];
        const p1 = [b[0] + swing[0] * off, b[1] + swing[1] * off];
        features.push({ type: "Feature", properties: { edgeKey, part: "window" }, geometry: { type: "LineString", coordinates: [toWorld(p0[0], p0[1]), toWorld(p1[0], p1[1])] } });
      }
      continue;
    }

    const hinge = a;
    const openTip = [hinge[0] + swing[0], hinge[1] + swing[1]];
    const startAngle = Math.atan2(b[1] - hinge[1], b[0] - hinge[0]);
    let endAngle = Math.atan2(swing[1], swing[0]);
    let delta = endAngle - startAngle;
    while (delta > Math.PI) delta -= 2 * Math.PI;
    while (delta < -Math.PI) delta += 2 * Math.PI;
    const arc = [];
    for (let i = 0; i <= ARC_STEPS; i += 1) {
      const angle = startAngle + delta * (i / ARC_STEPS);
      arc.push(toWorld(hinge[0] + Math.cos(angle), hinge[1] + Math.sin(angle)));
    }

    features.push({ type: "Feature", properties: { edgeKey, part: "leaf" }, geometry: { type: "LineString", coordinates: [toWorld(hinge[0], hinge[1]), toWorld(openTip[0], openTip[1])] } });
    features.push({ type: "Feature", properties: { edgeKey, part: "arc" }, geometry: { type: "LineString", coordinates: arc } });
  }
  return { type: "FeatureCollection", features };
}

// One filled polygon per cell, tagged with its room id, whether it's the safe
// room, and whether it's been breached (overrun by the horde). Same ring
// construction as baseShapeCellsToGeoJSON. `roomProps` (optional Map from
// room.id -> object) merges extra per-room properties onto every cell of that
// room — the Base tab uses it to carry regionKey/type for tinting + hit-testing.
export function roomsToGeoJSON(shape, rooms, safeRoomId, breachedRoomIds = [], roomProps = null) {
  const toWorld = makeToWorld(shape);
  const breached = breachedRoomIds instanceof Set ? breachedRoomIds : new Set(breachedRoomIds);
  const features = [];
  for (const room of rooms) {
    const extra = roomProps instanceof Map ? (roomProps.get(room.id) ?? {}) : {};
    for (const key of room.cellKeys) {
      const [row, col] = parseCellKey(key);
      const ring = [toWorld(col, row), toWorld(col + 1, row), toWorld(col + 1, row + 1), toWorld(col, row + 1)];
      features.push({
        type: "Feature",
        properties: { roomId: room.id, safe: room.id === safeRoomId, breached: breached.has(room.id), ...extra },
        geometry: { type: "Polygon", coordinates: [[...ring, ring[0]]] },
      });
    }
  }
  return { type: "FeatureCollection", features };
}

// The lattice midpoint of a door edge plus, for each side, the outward unit
// direction (in lattice units) toward that side's cell. A side cell not in
// `cells` is the exterior side (its direction points out of the base).
export function doorwaySides(cells, edgeKey) {
  const [aCell, bCell] = edgeCells(edgeKey);
  const { a, b } = latticeEndpoints(edgeKey);
  const mid = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
  const sides = {};
  const [kind] = edgeKey.split(":");
  // For a vertical edge the two sides are left/right (±col); for a horizontal
  // edge they're up/down (±row).
  if (kind === "v") {
    sides[cellKey(...aCell)] = [-1, 0];
    sides[cellKey(...bCell)] = [1, 0];
  } else {
    sides[cellKey(...aCell)] = [0, -1];
    sides[cellKey(...bCell)] = [0, 1];
  }
  return { mid, sides };
}

// [lon, lat] for a marker placed at a door, pushed `offsetCells` lattice units
// toward the side room containing `sideRoomCellKey` (or, if that cell isn't a
// door side, the first side). Defenders sit on the interior/deep side (~0.7);
// the horde masses on the approach side (~1.2).
export function doorwayAnchor(shape, cells, edgeKey, sideRoomCellKey, offsetCells) {
  const toWorld = makeToWorld(shape);
  const { mid, sides } = doorwaySides(cells, edgeKey);
  const direction = sides[sideRoomCellKey] ?? Object.values(sides)[0] ?? [0, 0];
  return toWorld(mid[0] + direction[0] * offsetCells, mid[1] + direction[1] * offsetCells);
}
